import type { AppScope } from "./scopeRouting";

/**
 * Ask Vivo answers come back with entity mentions already resolved into links
 * whose target is a `vivo:` URI — `vivo:subject/769-003`. The backend does the
 * resolving because only it can tell a site id from a count and only it knows
 * that the oncology trial prints `CA01-039` for a subject routed as
 * `DEMO-ONC-2026-01-CA01-039`. What it deliberately does not know is the URL
 * layout, so the kind-to-route mapping lives here.
 *
 * An unrecognised kind, or one that does not belong in the scope the reader is
 * in, resolves to null and renders as plain text. That is the intended
 * failure: a new entity kind reaching an older frontend costs a link, never a
 * broken page.
 */

export const VIVO_LINK_PREFIX = "vivo:";

export type VivoEntityKind =
  | "subject"
  | "site"
  | "trial"
  | "portfolio-site"
  | "investigator";

export interface VivoEntityRef {
  kind: VivoEntityKind;
  key: string;
}

const SCOPES: Record<VivoEntityKind, AppScope[]> = {
  subject: ["trial"],
  site: ["trial"],
  trial: ["portfolio"],
  "portfolio-site": ["portfolio"],
  investigator: ["portfolio"],
};

const PATHS: Record<VivoEntityKind, (key: string) => string> = {
  subject: (key) => `subjects/overview/${encodeURIComponent(key)}`,
  site: (key) => `sites/profile/${encodeURIComponent(key)}/overview`,
  trial: (key) => `trials/${encodeURIComponent(key)}`,
  "portfolio-site": (key) => `site-profiles/${encodeURIComponent(key)}`,
  investigator: (key) => `investigators/${encodeURIComponent(key)}`,
};

function isEntityKind(value: string): value is VivoEntityKind {
  return value in PATHS;
}

export function parseVivoEntityHref(
  href: string | undefined,
): VivoEntityRef | null {
  if (!href || !href.startsWith(VIVO_LINK_PREFIX)) return null;

  const body = href.slice(VIVO_LINK_PREFIX.length);
  const separator = body.indexOf("/");
  if (separator <= 0) return null;

  const kind = body.slice(0, separator);
  if (!isEntityKind(kind)) return null;

  let key: string;
  try {
    key = decodeURIComponent(body.slice(separator + 1));
  } catch {
    return null;
  }
  return key ? { kind, key } : null;
}

/**
 * The in-app route for an entity reference, or null when the reader's scope
 * has no page for it — a portfolio answer naming a subject cannot say which
 * trial's subject page to open, so it stays text.
 */
export function vivoEntityPath(
  ref: VivoEntityRef,
  scope: AppScope,
): string | null {
  return SCOPES[ref.kind].includes(scope) ? PATHS[ref.kind](ref.key) : null;
}
